import React, { useEffect, useState } from "react"
import { KButton, KTitleSpan, KSpan } from "kahuna-base-react-components"
import { announcements } from "../../../../apis"
import { Announcement } from "../../../../types"
import { toast } from "react-toastify"
import { ADMIN_PERMISSIONS, errorFilledToastOptions, successFilledToastOptions } from "../../../../constants"
import { checkAdminPermission, mapListToCamelCase, mapToCamelCase } from "../../../../utility"
import { connect } from "react-redux"
import { RootState } from "../../../../store"
import ConfirmationDialog from "../../../ui/modals/ConfirmationDialog"
import AnnouncementModal from "./AnnouncementModal"
import { DateTime } from "luxon"
import NotFound from "../../../NotFound"
import Loading from "../../../ui/Loading"
import { AnnouncementsIcon } from "../../../ui/icons"

type AdminAnnouncementsProps = {
  userAdminPermissions?: string
}

const AdminAnnouncements: React.FC<AdminAnnouncementsProps> = (props) => {
  const [loading, setLoading] = useState(false)
  const [announcementList, setAnnouncementList] = useState<Announcement[]>([])
  const [count, setCount] = useState(0)
  const [limit] = useState(20)
  const [offset, setOffset] = useState(0)
  const [idToDelete, setIdToDelete] = useState<number | undefined>(undefined)
  const [openModal, setOpenModal] = useState(false)
  const [selectedAnnouncement, setSelectedAnnouncement] = useState<Announcement | null>(null)
  const [error, setError] = useState<string | null>(null)

  const fetchAnnouncements = (currentOffset: number = 0) => {
    setLoading(true)
    setError(null)
    announcements(`/admin_list/?offset=${currentOffset}&limit=${limit}`, "get")
      .then((resp) => {
        if (resp.data.results) {
          setAnnouncementList(
            currentOffset === 0
              ? mapListToCamelCase(resp.data.results)
              : [...announcementList, ...mapListToCamelCase(resp.data.results)]
          )
          setCount(resp.data.count || 0)
        }
        setLoading(false)
      })
      .catch((err) => {
        setLoading(false)
        const errorMessage =
          err?.response?.data?.message ||
          "Error fetching announcements. Please check if the backend migration has been run."
        setError(errorMessage)
        toast.error(errorMessage, errorFilledToastOptions)
      })
  }

  const onDelete = () => {
    if (!idToDelete) return
    setLoading(true)

    announcements(`/admin_delete/?id=${idToDelete}`, "delete")
      .then((resp) => {
        if (resp.data.success) {
          setAnnouncementList(announcementList.filter((a) => a.id !== idToDelete))
          setCount(count - 1)
          toast.success("Announcement deleted successfully", successFilledToastOptions)
        } else {
          toast.error(resp.data.message || "Error deleting announcement", errorFilledToastOptions)
        }
        setLoading(false)
        setIdToDelete(undefined)
      })
      .catch((err) => {
        setLoading(false)
        setIdToDelete(undefined)
        toast.error(err?.response?.data?.message || "Error deleting announcement", errorFilledToastOptions)
      })
  }

  const onSave = (announcement: Partial<Announcement>) => {
    setLoading(true)
    const isEdit = !!announcement.id

    const endpoint = isEdit ? "/admin_update/" : "/admin_create/"
    const method = isEdit ? "put" : "post"

    announcements(endpoint, method, {
      id: announcement.id,
      title: announcement.title,
      description_en: announcement.descriptionEn,
      description_tr: announcement.descriptionTr,
      description_de: announcement.descriptionDe,
      end_date: announcement.endDate
    })
      .then((resp) => {
        if (resp.data.success) {
          if (isEdit) {
            setAnnouncementList(
              announcementList.map((a) => (a.id === announcement.id ? mapToCamelCase(resp.data.model) : a))
            )
          } else {
            setAnnouncementList([mapToCamelCase(resp.data.model), ...announcementList])
            setCount(count + 1)
          }
          toast.success(`Announcement ${isEdit ? "updated" : "created"} successfully`, successFilledToastOptions)
          setOpenModal(false)
          setSelectedAnnouncement(null)
        } else {
          toast.error(resp.data.message || "Error saving announcement", errorFilledToastOptions)
        }
        setLoading(false)
      })
      .catch((err) => {
        setLoading(false)
        toast.error(err?.response?.data?.message || "Error saving announcement", errorFilledToastOptions)
      })
  }

  useEffect(() => {
    fetchAnnouncements()
  }, [])

  const canWrite = checkAdminPermission(ADMIN_PERMISSIONS.USER_ASSET_WRITE, props?.userAdminPermissions)

  return (
    <div className="w-full">
      {loading && <Loading />}

      {/* Header */}
      <div className="flex flex-row justify-between items-center py-4 px-6 border-b border-gray-100">
        <div className="flex flex-row items-center gap-2">
          <div className="w-8 h-8 p-1.5 flex items-center justify-center shrink-0">{AnnouncementsIcon("#111")}</div>
          <div className="flex flex-row items-center pt-0.5 whitespace-nowrap">
            <KTitleSpan
              text={`Announcements (${count})`}
              fontSize={20}
              color="#111"
              lineHeight="28px"
              fontWeight={500}
            />
          </div>
        </div>
        <div className="flex gap-3 items-center">
          <KButton
            text="Refresh"
            height="36px"
            width="120px"
            background="#F5F5F5"
            hoverBackground="#E5E5E5"
            onClick={() => {
              setOffset(0)
              fetchAnnouncements(0)
            }}
            disabled={loading}
          />
          {canWrite && (
            <KButton
              text="+ Add Announcement"
              height="36px"
              width="200px"
              background="#111"
              hoverBackground="#333"
              textColor="#FFF"
              onClick={() => {
                setSelectedAnnouncement(null)
                setOpenModal(true)
              }}
            />
          )}
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        {error && !loading && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
            <KSpan text={error} color="#DC2626" />
          </div>
        )}

        {!loading && !error && announcementList.length === 0 && (
          <div className="flex w-full h-full items-center justify-center pt-[100px]">
            <NotFound
              title="No Announcements"
              description="No announcements have been created yet. Click 'Add Announcement' to create one."
              imageWidth={250}
            />
          </div>
        )}

        {announcementList.length > 0 && (
          <div className="grid gap-4">
            {announcementList.map((announcement) => (
              <div
                key={announcement.id}
                className="bg-white border border-gray-200 rounded-xl p-5 hover:shadow-md transition-shadow"
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <KTitleSpan text={announcement.title} fontSize={16} lineHeight="24px" fontWeight={600} />
                      <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded">
                        ID: {announcement.id}
                      </span>
                    </div>

                    <div className="grid grid-cols-3 gap-4 mt-3">
                      <div>
                        <KSpan text="English" color="#666" fontSize={12} />
                        <p className="text-sm text-gray-700 mt-1 line-clamp-2">{announcement.descriptionEn || "-"}</p>
                      </div>
                      <div>
                        <KSpan text="Turkish" color="#666" fontSize={12} />
                        <p className="text-sm text-gray-700 mt-1 line-clamp-2">{announcement.descriptionTr || "-"}</p>
                      </div>
                      <div>
                        <KSpan text="German" color="#666" fontSize={12} />
                        <p className="text-sm text-gray-700 mt-1 line-clamp-2">{announcement.descriptionDe || "-"}</p>
                      </div>
                    </div>

                    <div className="flex gap-6 mt-4 text-xs text-gray-500">
                      <span>
                        Created:{" "}
                        {announcement.created
                          ? DateTime.fromISO(announcement.created).toFormat("dd MMM yyyy, HH:mm")
                          : "-"}
                      </span>
                      <span>
                        End Date:{" "}
                        {announcement.endDate
                          ? DateTime.fromISO(announcement.endDate).toFormat("dd MMM yyyy")
                          : "No expiration"}
                      </span>
                    </div>
                  </div>

                  {canWrite && (
                    <div className="flex gap-2 ml-4">
                      <KButton
                        text="Edit"
                        height="32px"
                        padding="8px 16px"
                        background="#F5F5F5"
                        hoverBackground="#E5E5E5"
                        onClick={() => {
                          setSelectedAnnouncement(announcement)
                          setOpenModal(true)
                        }}
                      />
                      <KButton
                        text="Delete"
                        height="32px"
                        padding="8px 16px"
                        background="#FEE2E2"
                        hoverBackground="#FECACA"
                        textColor="#DC2626"
                        onClick={() => setIdToDelete(announcement.id)}
                      />
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {announcementList.length > 0 && announcementList.length < count && (
          <div className="flex justify-center py-6">
            <KButton
              text="Load More"
              width="auto"
              height="40px"
              disabled={loading}
              background="#FFF"
              hoverBackground="#F7F7F7"
              onClick={() => {
                const newOffset = offset + limit
                setOffset(newOffset)
                fetchAnnouncements(newOffset)
              }}
            />
          </div>
        )}
      </div>

      <ConfirmationDialog
        openConfirmation={idToDelete !== undefined}
        setOpenConfirmation={() => setIdToDelete(undefined)}
        loading={loading}
        handleConfirm={onDelete}
        overrideDescription="Are you sure you want to delete this announcement? This action cannot be undone."
      />

      <AnnouncementModal
        open={openModal}
        announcement={selectedAnnouncement}
        onClose={() => {
          setOpenModal(false)
          setSelectedAnnouncement(null)
        }}
        onSave={onSave}
        loading={loading}
      />
    </div>
  )
}

const mapStateToProps = (state: RootState) => {
  return {
    userAdminPermissions: state.auth.user.adminPermissions
  }
}

export default connect(mapStateToProps, {})(AdminAnnouncements)
